import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback } from 'react';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AlertPage } from '../-shared/AlertPage';
import { createSearchSchema } from '../-shared/search-schema';
import type { AlertRowWithUser, AlertSearchParams } from '../-shared/types';
import { createAlertExportHook, createAlertQueryHook } from '../-shared/useAlertQuery';
import { SoftwareAlertType } from '../../-zod-schema';
import { ColumnKey, SortableColumnKey, useSoftwareAlertsInactiveUserColumns } from './-hooks/useColumns';
const SearchSchema = createSearchSchema(SortableColumnKey);
const useAlertQuery = createAlertQueryHook<AlertRowWithUser>({
  category: 'INACTIVE',
  queryKeySegment: 'inactive-user',
  sortColumnMap: {
    [ColumnKey.appUser]: 'USER',
    [ColumnKey.app]: 'APP',
    [ColumnKey.worker]: 'PERSON',
    user_actualRoleName: 'APP_USER_ROLE',
    user_statusName: 'APP_USER_STATUS',
    user_lastSignedInAt: 'LAST_SIGNED_IN_AT',
    user_createdAt: 'CREATED_AT'
  },
  filterAndMap: (items): AlertRowWithUser[] => items.filter(item => item.user).map(item => ({
    ...item,
    user: item.user as NonNullable<typeof item.user>
  }))
});
const useExport = createAlertExportHook('INACTIVE');
export const Route = createFileRoute('/_staff/software/_layout/alerts/_alerts/inactive-user/')({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsInactiveUser
});
function SoftwareAlertsInactiveUser() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const counts = useLoaderData({
    from: Route.parentRoute.id
  });
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.alert'
  });
  const {
    data,
    isPending
  } = useAlertQuery(search);
  const {
    mutate: onExport,
    isPending: exporting
  } = useExport(search);
  const columns = useSoftwareAlertsInactiveUserColumns();
  const onSearch = useCallback((query: Partial<AlertSearchParams>) => {
    navigate({
      search: prev => ({
        ...prev,
        ...query
      }) as typeof prev
    });
  }, [navigate]);
  return <AlertPage alertTypeKey={SoftwareAlertType.inactive.key} helpTitle={t('help.inactive.title')} helpText={t('help.inactive.text')} counts={counts} search={search} onSearch={onSearch} data={data} isPending={isPending} exporting={exporting} onExport={onExport} columns={columns} />;
}